/**
 * Ringtone Service
 *
 * Plays ring tones for incoming/outgoing calls.
 * Supports built-in presets + custom user-uploaded sounds.
 * Loops while ringing, stops on answer/hang-up.
 *
 * Prefs stored per-user in Firestore `userProfiles/{uid}.ringtonePrefs`
 * and platform default in `siteSettings/notifications.defaultRingtone`.
 */

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// ─── Built-in ringtone presets ──────────────────────────────────────────────

export interface RingtonePreset {
  id: string;
  label: string;
  url: string;
}

export const RINGTONE_PRESETS: RingtonePreset[] = [
  { id: 'rotary',     label: 'Rotary Phone', url: '/sounds/rings/rotary.wav' },
  { id: 'classic',    label: 'Classic',       url: '/sounds/rings/classic.wav' },
  { id: 'digital',    label: 'Digital',       url: '/sounds/rings/digital.wav' },
  { id: 'marimba',    label: 'Marimba',       url: '/sounds/rings/marimba.wav' },
  { id: 'soft-pulse', label: 'Soft Pulse',    url: '/sounds/rings/soft-pulse.wav' },
  { id: 'retro',      label: 'Retro 8-bit',   url: '/sounds/rings/retro.wav' },
];

export const DEFAULT_RINGTONE_ID = 'rotary';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface RingtonePrefs {
  enabled: boolean;
  ringtoneId: string;        // preset id or 'custom'
  customRingtoneUrl?: string; // Firebase Storage URL if custom
  volume: number;            // 0–100
}

export const DEFAULT_RINGTONE_PREFS: RingtonePrefs = {
  enabled: true,
  ringtoneId: DEFAULT_RINGTONE_ID,
  volume: 80,
};

// ─── Looping ring playback ─────────────────────────────────────────────────

let activeRingAudio: HTMLAudioElement | null = null;

function getRingtoneUrl(prefs: RingtonePrefs): string {
  if (prefs.ringtoneId === 'custom' && prefs.customRingtoneUrl) {
    return prefs.customRingtoneUrl;
  }
  const preset = RINGTONE_PRESETS.find((p) => p.id === prefs.ringtoneId);
  return preset?.url ?? RINGTONE_PRESETS[0].url;
}

/** Start ringing — loops until stopRingtone() is called. */
export function startRingtone(prefs: RingtonePrefs): void {
  stopRingtone(); // stop any currently playing ring

  if (!prefs.enabled || prefs.volume <= 0) return;

  const url = getRingtoneUrl(prefs);
  try {
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = Math.min(1, Math.max(0, prefs.volume / 100));
    audio.play().catch(() => {});
    activeRingAudio = audio;
  } catch {
    // ignore
  }
}

/** Stop the currently playing ringtone. */
export function stopRingtone(): void {
  if (activeRingAudio) {
    activeRingAudio.pause();
    activeRingAudio.currentTime = 0;
    activeRingAudio = null;
  }
}

/** Preview a ringtone by id (plays once, not looped). */
export function previewRingtone(ringtoneId: string, customUrl?: string, volume = 80): void {
  stopRingtone();
  const url = ringtoneId === 'custom' && customUrl
    ? customUrl
    : RINGTONE_PRESETS.find((p) => p.id === ringtoneId)?.url ?? RINGTONE_PRESETS[0].url;
  const audio = new Audio(url);
  audio.volume = Math.min(1, Math.max(0, volume / 100));
  audio.play().catch(() => {});
  // Auto-stop after 4 seconds for previews
  setTimeout(() => { audio.pause(); audio.currentTime = 0; }, 4000);
}

// ─── Firestore persistence ─────────────────────────────────────────────────

/** Get user's ringtone prefs (falls back to platform default, then built-in). */
export async function getRingtonePrefs(uid: string): Promise<RingtonePrefs> {
  const userSnap = await getDoc(doc(db, 'userProfiles', uid));
  const userData = userSnap.data();
  if (userData?.ringtonePrefs) {
    return { ...DEFAULT_RINGTONE_PREFS, ...userData.ringtonePrefs };
  }

  // Platform default
  const siteSnap = await getDoc(doc(db, 'siteSettings', 'notifications'));
  const siteData = siteSnap.data();
  if (siteData?.defaultRingtone) {
    return { ...DEFAULT_RINGTONE_PREFS, ringtoneId: siteData.defaultRingtone };
  }

  return { ...DEFAULT_RINGTONE_PREFS };
}

/** Save user's ringtone prefs. */
export async function saveRingtonePrefs(uid: string, prefs: RingtonePrefs): Promise<void> {
  await setDoc(doc(db, 'userProfiles', uid), { ringtonePrefs: prefs }, { merge: true });
}

/** Save platform default ringtone (Super Admin). */
export async function savePlatformDefaultRingtone(ringtoneId: string): Promise<void> {
  await setDoc(doc(db, 'siteSettings', 'notifications'), {
    defaultRingtone: ringtoneId,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/** Get platform default ringtone id. */
export async function getPlatformDefaultRingtone(): Promise<string> {
  const snap = await getDoc(doc(db, 'siteSettings', 'notifications'));
  return snap.data()?.defaultRingtone ?? DEFAULT_RINGTONE_ID;
}

/** Upload a custom ringtone to Firebase Storage. Returns download URL. */
export async function uploadCustomRingtone(uid: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'mp3';
  const path = `notification-sounds/${uid}/ringtone-${Date.now()}.${ext}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return getDownloadURL(fileRef);
}
